// Lib
import { Matrix3D } from 'rematrix';
import React, { RefObject } from 'react';
import BezierEasing from 'bezier-easing';

// Utils
import Mousetrap from '../../workspace/shortcuts/Mousetrap';
import { sendAmplitudeEvent } from '../../analytics/amplitudeService';
import * as matrixLib from '../../../common/maths/matrix/matrixUtils';
import { getTransformationOrigin } from './zoomTransformationPointUtils';
import { getUpdatedTransformationMatrix } from './zoomTransformationUtils';
import {
    calculateZoomToFitResetCanvasProperties,
    getTargetZoomToFitScale,
    getZoomToFitEventPosition,
    getZoomToFitScale,
    getZoomToFitZoomPointMode,
} from './utils/zoomToFitUtils';
import { renderScrollAndTransformation, renderTransformationMatrixCSS } from './zoomTransformationRenderUtils';
import requestSecondAnimationFrame from '../../../common/utils/lib/requestSecondAnimationFrame';
import requestPromisedAnimationFrame from '../../../common/utils/lib/requestPromisedAnimationFrame';

// Singletons
import zoomStateSingleton from './zoomStateSingleton';

// Types
import { Point } from '../../../common/maths/geometry/pointTypes';
import { Rect, RectSize } from '../../../common/maths/geometry/rect/rectTypes';
import { ZoomDomElementPropertyCache } from './zoomDomElementPropertyCache';

// Constants
import { ZOOM_MODES } from './zoomConstants';
import { AMPLITUDE_USER_PROPS, ANALYTICS_ZOOM_TYPES, TRACKED_FEATURES } from '../../../common/analytics/statsConstants';

/**
 * Records an event in amplitude every time a zoom to fit occurs.
 */
const sendAmplitudeZoomToFitEvent = (targetScale: number): void => {
    let analyticsZoomType = ANALYTICS_ZOOM_TYPES.RESET_ZOOM;

    if (targetScale > 1) {
        analyticsZoomType = ANALYTICS_ZOOM_TYPES.ZOOM_IN;
    } else if (targetScale < 1) {
        analyticsZoomType = ANALYTICS_ZOOM_TYPES.ZOOM_OUT;
    }

    sendAmplitudeEvent({
        eventType: 'Zoom to fit',
        eventProperties: {
            zoomType: analyticsZoomType,
        },
        userProperties: {
            [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.ZOOM_TO_FIT]: true },
        },
    });
};

/**
 * The bezier easing function to use for the zoom to fit animation.
 */
const zoomToFitBezierFunc = BezierEasing(0.645, 0.045, 0.355, 1);

/**
 * Gets the next scale based on the current ratio between the start and end time.
 */
const updateScale = (t: number, initialScale: number, targetScale: number) => {
    if (t > 1) return targetScale;

    // NOTE: This is the same bezier curve used for the old scale functionality
    const factor = zoomToFitBezierFunc(t);

    let nextScale = initialScale + (targetScale - initialScale) * factor;

    // If the next scale is less than .25% from the target scale, and also less than .001 from the target value
    // then we're close enough to use the target scale
    const scaleDiff = Math.abs(nextScale - targetScale);
    const isCloseEnoughToTargetScale = scaleDiff < 0.001 && scaleDiff / targetScale < 0.0025;

    const shouldUseTargetScale =
        // If we're close enough to the target scale, just use the target scale
        isCloseEnoughToTargetScale ||
        // Or we have overshot the target scale
        (initialScale < targetScale && nextScale > targetScale) ||
        // Or we have undershot the target scale
        (initialScale > targetScale && nextScale < targetScale);

    if (shouldUseTargetScale) {
        nextScale = targetScale;
    }

    return nextScale;
};

interface ZoomToFitHandlerProps {
    dispatchGetZoomToFitContentRectThunk: (
        canvasViewportNode: HTMLElement | null,
        zoomScaleOverride?: number,
        zoomTranslationOverride?: Point,
    ) => Rect;
    dispatchSetCanvasZoomTransformation: (scrollAreaSize: RectSize, mode: string | null) => void;
    canvasViewportRef: RefObject<HTMLDivElement>;
    canvasDocumentSize: RectSize;
    scrollAreaRef: RefObject<HTMLDivElement>;
    canvasDocumentRef: RefObject<HTMLDivElement>;
    domElementPropertyCache: ZoomDomElementPropertyCache;
    updateViewUtilZoom: (nextScale: number, targetScale: number) => void;
    getZoomCanvasTransformationMatrix: () => Matrix3D;
    getZoomCanvasTransformationPoint: () => Point;
    onZoomToFitStart: () => void;
    onZoomToFitEnd: (transformationMatrix: Matrix3D, transformationPoint: Point) => void;
}

/**
 * Handles the Zoom to Fit logic, such as the key bindings and the animation.
 */
class ZoomToFitHandler extends React.Component<ZoomToFitHandlerProps> {
    private isZoomingToFit = false;
    private transformationMatrix: Matrix3D;
    private transformationPoint: Point;
    private prevContentRect?: Rect;

    constructor(props: ZoomToFitHandlerProps) {
        super(props);

        this.transformationMatrix = props.getZoomCanvasTransformationMatrix();
        this.transformationPoint = props.getZoomCanvasTransformationPoint();
    }

    componentDidMount(): void {
        Mousetrap.bind('z', this.zoomToFit);
    }

    componentWillUnmount(): void {
        Mousetrap.unbind('z');
    }

    /**
     * Initiates the zoom to fit operation, determining the transformation point and content bounding rect.
     */
    zoomToFit = (): void => {
        const {
            dispatchGetZoomToFitContentRectThunk,
            canvasViewportRef,
            canvasDocumentSize,
            domElementPropertyCache,
            onZoomToFitStart,
            getZoomCanvasTransformationMatrix,
            getZoomCanvasTransformationPoint,
        } = this.props;

        if (zoomStateSingleton.getIsZooming()) return;
        if (this.isZoomingToFit) return;
        if (!canvasViewportRef.current) return;

        const zoomCanvasTransformationPoint = getZoomCanvasTransformationPoint();
        this.transformationMatrix = getZoomCanvasTransformationMatrix();
        const currentScale = matrixLib.getScale(this.transformationMatrix);

        const currentTranslation = matrixLib.getTranslation(this.transformationMatrix);

        // First determine what the ideal scale is (this could be > 1 if there's not much content).
        const contentRect = dispatchGetZoomToFitContentRectThunk(
            canvasViewportRef.current,
            currentScale,
            currentTranslation,
        );
        const { canvasViewportClientRect, canvasScroll } = domElementPropertyCache.getCachedValues(
            this.props.canvasViewportRef.current,
        );

        // Don't allow zoom to fit to occur on empty boards
        if (!contentRect.width || !contentRect.height) return;

        onZoomToFitStart();

        const zoomToFitScale = getZoomToFitScale(contentRect, canvasViewportClientRect);

        // Then if we're already within some margin of that we should return to 100%
        const targetScale = getTargetZoomToFitScale(currentScale, zoomToFitScale);

        sendAmplitudeZoomToFitEvent(targetScale);

        const zoomPointMode = getZoomToFitZoomPointMode(
            currentScale,
            targetScale,
            zoomCanvasTransformationPoint,
            this.transformationPoint,
            contentRect,
            this.prevContentRect,
        );

        this.prevContentRect = contentRect;

        // FIXME choose a different transformation point if an element is selected
        // We don't use the event position in this flow (yet)
        // NOTE: We may want to in the future if we zoom to a selected element
        const eventPosition = getZoomToFitEventPosition(
            zoomCanvasTransformationPoint,
            this.transformationPoint,
            zoomPointMode,
        );

        this.transformationPoint = getTransformationOrigin(
            zoomPointMode,
            this.transformationPoint,
            eventPosition,
            canvasViewportClientRect,
            canvasScroll,
            canvasDocumentSize,
            contentRect,
            this.transformationMatrix,
            targetScale,
        );

        canvasViewportRef.current.classList.add('zooming-to-fit');
        document.body.classList.add('zooming-to-fit');

        // Improve the zoom to fit animation by fading out the grid early
        if (targetScale !== 1) {
            canvasViewportRef.current.classList.add('hide-grid');
        }

        this.isZoomingToFit = true;
        zoomStateSingleton.setIsZooming(true);

        this.animateZoomToFit(targetScale);
    };

    /**
     * Uses a set duration and interpolation curve to render individual frames of the scale animation
     * and then commits the result to the store.
     */
    animateZoomToFit = async (targetScale: number): Promise<void> => {
        const { updateViewUtilZoom } = this.props;

        const startTime = Date.now();
        const duration = 600;

        let t = 0;
        const initialScale = matrixLib.getScale(this.transformationMatrix);
        let currentScale = initialScale;

        while (currentScale !== targetScale) {
            t = (Date.now() - startTime) / duration;
            currentScale = updateScale(t, initialScale, targetScale);

            // Update the DOM for the View util & Zoom popup using vanilla JS
            updateViewUtilZoom(currentScale, targetScale);

            await requestPromisedAnimationFrame(() => {
                this.renderZoomToFitFrame(currentScale);
            });
        }

        this.commitZoomToFit();
    };

    /**
     * Renders a single frame of the zoom to fit animation.
     */
    renderZoomToFitFrame = (scale: number): void => {
        const { canvasDocumentRef, domElementPropertyCache } = this.props;
        const { canvasScroll } = domElementPropertyCache.getCachedValues(this.props.canvasViewportRef.current);

        this.transformationMatrix = getUpdatedTransformationMatrix(
            scale,
            this.transformationPoint,
            this.transformationMatrix,
            canvasScroll,
        );

        renderTransformationMatrixCSS(canvasDocumentRef.current, this.transformationMatrix);
    };

    /**
     * Commits the new zoom to fit state to the store.
     */
    commitZoomToFit = (): void => {
        if (!this.isZoomingToFit) return;

        const {
            dispatchGetZoomToFitContentRectThunk,
            dispatchSetCanvasZoomTransformation,
            canvasViewportRef,
            canvasDocumentSize,
            scrollAreaRef,
            canvasDocumentRef,
            domElementPropertyCache,
            onZoomToFitEnd,
        } = this.props;

        const currentScale = matrixLib.getScale(this.transformationMatrix);
        const currentTranslation = matrixLib.getTranslation(this.transformationMatrix);

        // First determine what the ideal scale is (this could be > 1 if there's not much content).
        const contentRect = dispatchGetZoomToFitContentRectThunk(
            canvasViewportRef.current,
            currentScale,
            currentTranslation,
        );
        const { canvasViewportClientRect, canvasScroll } = domElementPropertyCache.getCachedValues(
            this.props.canvasViewportRef.current,
        );

        const updatedCanvasProperties = calculateZoomToFitResetCanvasProperties(
            this.transformationPoint,
            this.transformationMatrix,
            canvasViewportClientRect,
            canvasDocumentSize,
            contentRect,
            canvasScroll,
        );

        this.transformationMatrix = updatedCanvasProperties.transformationMatrix;
        this.transformationPoint = updatedCanvasProperties.transformationPoint;

        // Update the scroll area and transformation in vanilla JS to avoid render glitches
        renderScrollAndTransformation(
            canvasViewportRef.current,
            scrollAreaRef.current,
            canvasDocumentRef.current,
            updatedCanvasProperties.scrollAreaSize,
            updatedCanvasProperties.canvasScroll,
            this.transformationMatrix,
            'zooming-to-fit',
        );

        // This follows the same pattern as ZoomCanvas.commitZoom - we need to make sure that the state update
        // occurs after the vanilla JS update
        requestSecondAnimationFrame(() => {
            onZoomToFitEnd(updatedCanvasProperties.transformationMatrix, updatedCanvasProperties.transformationPoint);

            const currentScale = matrixLib.getScale(updatedCanvasProperties.transformationMatrix);
            const zoomMode = currentScale !== 1 ? ZOOM_MODES.ZOOM_TO_FIT : null;

            dispatchSetCanvasZoomTransformation(updatedCanvasProperties.scrollAreaSize, zoomMode);

            this.isZoomingToFit = false;
            zoomStateSingleton.setIsZooming(false);
        });
    };

    render(): null {
        return null;
    }
}

export default ZoomToFitHandler;
